export default {
  methods: {
    getMainGroupContainer(name) {
      return document.getElementById(name)
    },
    getAllSelectablePaths(container) {
      return Array.from(container.querySelectorAll('path[id]'))
        .filter(el => !el.classList.contains('unavailable'))
        .map(el => el.getAttribute('id'))
    },
    buildAdjacencyMap(ids) {
      const map = {}
      ids.forEach(id => { map[id] = [] })
      for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
          if (this.areAdjacentById(ids[i], ids[j])) {
            map[ids[i]].push(ids[j])
            map[ids[j]].push(ids[i])
          }
        }
      }
      return map
    },
    growCluster(start, count, adjacencyMap) {
      const selected = new Set([start])
      const frontier = new Set(adjacencyMap[start])

      while (selected.size < count && frontier.size > 0) {
        let best = null
        let bestScore = -1
        const candidates = Array.from(frontier).sort()

        for (const candidate of candidates) {
          const score = adjacencyMap[candidate].filter(n => selected.has(n)).length
          if (score > bestScore) {
            bestScore = score
            best = candidate
          }
        }

        if (best === null) break

        selected.add(best)
        frontier.delete(best)
        adjacencyMap[best].forEach(n => {
          if (!selected.has(n)) frontier.add(n)
        })
      }

      return selected.size === count ? Array.from(selected) : null
    },
    scoreCluster(ids, adjacencyMap) {
      let internalEdges = 0
      const idSet = new Set(ids)
      ids.forEach(id => {
        internalEdges += adjacencyMap[id].filter(n => idSet.has(n)).length
      })
      return internalEdges
    },
    // NEU: wächst eine Insel bis maximal 'maxSize', ohne exakte Treffer zu verlangen
    growClusterUpTo(start, maxSize, adjacencyMap) {
      const selected = new Set([start])
      const frontier = new Set(adjacencyMap[start])

      while (selected.size < maxSize && frontier.size > 0) {
        let best = null
        let bestScore = -1
        const candidates = Array.from(frontier).sort()

        for (const candidate of candidates) {
          const score = adjacencyMap[candidate].filter(n => selected.has(n)).length
          if (score > bestScore) {
            bestScore = score
            best = candidate
          }
        }

        if (best === null) break

        selected.add(best)
        frontier.delete(best)
        adjacencyMap[best].forEach(n => {
          if (!selected.has(n)) frontier.add(n)
        })
      }

      return Array.from(selected)
    },

    // findBestGroup bekommt einen 'silent'-Modus, damit der Fallback nicht
    // fälschlich einen Fehler-Toast zeigt, bevor der Mehrtisch-Versuch läuft
    findBestGroup(mainGroupName, count, { silent = false } = {}) {
      const container = this.getMainGroupContainer(mainGroupName)
      if (!container) {
        if (!silent) this.showError(`Gruppe "${mainGroupName}" wurde nicht gefunden.`)
        return null
      }

      const allIds = this.getAllSelectablePaths(container).sort()

      if (allIds.length < count) {
        if (!silent) this.showError(`In dieser Gruppe gibt es nicht genug verfügbare Plätze (benötigt: ${count}, verfügbar: ${allIds.length}).`)
        return null
      }

      const adjacencyMap = this.buildAdjacencyMap(allIds)
      const validStarts = allIds.filter(id => this.canStartSelection(id))

      let bestSet = null
      let bestScore = -1
      let bestKey = null

      validStarts.forEach(start => {
        const cluster = this.growCluster(start, count, adjacencyMap)
        if (!cluster) return

        const score = this.scoreCluster(cluster, adjacencyMap)
        const key = [...cluster].sort().join(',')

        if (score > bestScore || (score === bestScore && (bestKey === null || key < bestKey))) {
          bestScore = score
          bestSet = cluster
          bestKey = key
        }
      })

      if (!bestSet && !silent) {
        this.showError(`Es konnte keine passende Auswahl mit ${count} zusammenhängenden Plätzen gefunden werden.`)
      }

      return bestSet
    },

    // NEU: verteilt die Anzahl notfalls über mehrere getrennte Tische/Inseln
    findBestMultiIslandGroup(mainGroupName, count) {
      const container = this.getMainGroupContainer(mainGroupName)
      if (!container) {
        this.showError(`Gruppe "${mainGroupName}" wurde nicht gefunden.`)
        return null
      }

      let pool = this.getAllSelectablePaths(container).sort()
      if (pool.length < count) {
        this.showError(`In dieser Gruppe gibt es nicht genug verfügbare Plätze (benötigt: ${count}, verfügbar: ${pool.length}).`)
        return null
      }

      const result = []
      let remaining = count

      while (remaining > 0) {
        const adjacencyMap = this.buildAdjacencyMap(pool)
        const validStarts = pool.filter(id => this.canStartSelection(id))

        if (validStarts.length === 0) {
          this.showError('Es konnten nicht genügend gültige Plätze für die restliche Anzahl gefunden werden.')
          return null
        }

        let bestIsland = null
        validStarts.forEach(start => {
          const island = this.growClusterUpTo(start, remaining, adjacencyMap)
          if (!bestIsland || island.length > bestIsland.length) {
            bestIsland = island
          }
        })

        if (!bestIsland || bestIsland.length === 0) {
          this.showError('Es konnte keine gültige Platzgruppe mehr gebildet werden.')
          return null
        }

        result.push(...bestIsland)
        remaining -= bestIsland.length
        pool = pool.filter(id => !bestIsland.includes(id))
      }

      return result
    },

    selectBestGroup(count) {
      if (!count || count < 1) {
        this.showError('Bitte eine gültige Anzahl angeben.')
        return
      }

      // Zuerst einen einzigen zusammenhängenden Block versuchen (silent, damit
      // bei Fehlschlag kein störender Toast kommt, bevor der Fallback läuft)
      let bestSet = this.findBestGroup(this.initialCategory, count, { silent: true })

      // Reicht ein Tisch/eine Tafel nicht aus: auf mehrere Tische verteilen
      if (!bestSet) {
        bestSet = this.findBestMultiIslandGroup(this.initialCategory, count)
      }

      if (!bestSet) return

      this.clearSelection()

      bestSet.forEach(id => {
        const ok = this.selectElementById(id) // allowNewIsland default true
        if (!ok) {
          this.showError(`Platz ${id} konnte nicht ausgewählt werden.`)
        }
      })
    },
  }
}